import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Check, X, AlertCircle, DollarSign, Calendar, TrendingUp, Info,
  ArrowRight, Search, GraduationCap, BookOpen, CalendarDays,
} from 'lucide-react';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import { useSeoMeta } from '@/pages/learn/LearnPage';
import { useSeoStockModal } from '@/components/SeoStockModal';
import FixedRateAssetCard, { isFixedRateAsset } from '@/components/FixedRateAssetCard';
import SplitNote from '@/components/SplitNote';
import { DeclaredPill, EstimateDisclaimer } from '@/components/DeclaredPill';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Is [TICKER] a dividend stock?" — programmatic SEO landing (Feb 2026).
 *
 * Route: /is/:ticker/a-dividend-stock
 *
 * One page per cached ticker (~5,350 pages). Targets the long-tail
 * informational query "is X a dividend stock" / "does X pay a
 * dividend". Data comes from the lean /api/stocks/:ticker/dividend-check
 * endpoint which reads directly from stock_cache (no self-heal side
 * effects — Googlebot friendly).
 *
 * Page structure:
 *   H1 → verdict (Yes / No / MMF / Mutual Fund) with ticker in URL
 *   Verdict card → the answer above the fold
 *   Key numbers card (yield, rate, frequency, dates OR why-not reason)
 *   Peer alternatives block → internal links to /is/{peer}/a-dividend-stock
 *   Related actions → Screener, Learn guides, Dashboard
 *   FAQ section → JSON-LD schema for rich snippets
 *
 * SEO conventions:
 *   • Canonical URL uses LOWERCASE ticker to match the sitemap
 *   • <title>: "Is [TICKER] a dividend stock? — DiviDrip"
 *   • <meta description>: dynamic based on verdict
 *   • Ticker displayed in UPPERCASE (financial convention) despite
 *     lowercase URL
 */
export default function IsDividendStock() {
  const { openStock, modal: stockModal } = useSeoStockModal();
  const { ticker: tickerParam } = useParams();
  const ticker = (tickerParam || '').toUpperCase().trim();
  const tickerLower = ticker.toLowerCase();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ticker) {
      navigate('/', { replace: true });
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/stocks/${ticker}/dividend-check`)
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.status === 404 ? 'not-tracked' : 'fetch-failed');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [ticker, navigate]);

  // Build SEO metadata from the verdict. Description must be under
  // ~155 chars for Google to show the full snippet.
  const seoTitle = data
    ? (data.verdict === 'dividend'
        ? `Is ${ticker} a dividend stock? Yes — current yield, ex-div & payout | DiviDrip`
        : data.verdict === 'money_market_fund'
          ? `Is ${ticker} a dividend stock? It's a Money Market Fund | DiviDrip`
          : data.verdict === 'mutual_fund'
            ? `Is ${ticker} a dividend stock? It's a Mutual Fund | DiviDrip`
            : `Is ${ticker} a dividend stock? No — here's why + alternatives | DiviDrip`)
    : `Is ${ticker} a dividend stock? | DiviDrip`;

  const seoDescription = data
    ? (data.verdict === 'dividend'
        ? `${ticker} (${data.name}) pays a dividend. Yield ${fmtPct(data.dividend_yield)}, rate $${fmtNum(data.dividend_rate)}, ${data.payment_frequency || 'regular'} payments. Full history + peers.`
        : data.verdict === 'money_market_fund'
          ? `${ticker} is a money market fund. Current 7-day yield ${fmtPct(data.mmf_7day_yield)}. Learn how MMFs pay interest vs traditional dividends.`
          : data.verdict === 'mutual_fund'
            ? `${ticker} is an open-end mutual fund. DiviDrip does not track mutual fund distributions — see how single-X mutual fund tickers work and where to find the fund's own distribution history.`
            : data.verdict === 'structural_non_dividend'
            ? `${ticker} does not pay a dividend — it's a ${data.non_dividend_security_class?.toUpperCase() || 'non-dividend security'}. See 4 dividend-paying alternatives in the same sector.`
            : `${ticker} (${data.name}) does not currently pay a dividend. Here's why and 4 dividend-paying alternatives from the same sector.`)
    : `Find out whether ${ticker} pays a dividend. Live yield, rate, ex-dividend date and alternatives from DiviDrip's 5,350+ ticker database.`;

  useSeoMeta({
    title: seoTitle,
    description: seoDescription,
    canonical: `https://twylightcrow.com/is/${tickerLower}/a-dividend-stock`,
  });

  // JSON-LD FAQ schema for rich snippets. Injects on mount, removes on
  // unmount. Google reads client-rendered structured data.
  useEffect(() => {
    if (!data) return;
    const faqs = buildFaqs(ticker, data);
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    script.setAttribute('data-testid', 'is-dividend-jsonld');
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [ticker, data]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12" data-testid="is-dividend-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-72 bg-slate-200 dark:bg-slate-800 rounded" />
          <div className="h-32 bg-slate-200 dark:bg-slate-800 rounded" />
          <div className="h-64 bg-slate-200 dark:bg-slate-800 rounded" />
        </div>
      </div>
    );
  }

  if (error === 'not-tracked') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16" data-testid="is-dividend-notfound">
        <BackOrHomeButton />
        <h1 className="text-3xl font-bold mt-6 mb-3 text-slate-900 dark:text-slate-100">
          {ticker} isn&apos;t in our database yet.
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mb-8">
          We track 5,350+ dividend-paying and non-dividend tickers, but {ticker} isn&apos;t one of them yet. Try our screener to find similar tickers, or browse popular dividend stocks below.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/screener" className="px-5 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg" data-testid="is-dividend-notfound-screener">
            Open Screener
          </Link>
          <Link to="/" className="px-5 py-2.5 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="is-dividend-notfound-home">
            Popular dividend stocks
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16" data-testid="is-dividend-error">
        <BackOrHomeButton />
        <h1 className="text-2xl font-bold mt-6 mb-3">Something went wrong loading {ticker}.</h1>
        <p className="text-slate-600 dark:text-slate-400">Please try again in a moment.</p>
      </div>
    );
  }

  const isDividend = data.verdict === 'dividend';
  const isMmf = data.verdict === 'money_market_fund';
  const isMutualFund = data.verdict === 'mutual_fund' || !!data.is_mutual_fund;
  const isNonDiv = data.verdict === 'non_dividend' || data.verdict === 'structural_non_dividend';

  return (
    <>
    <div className="max-w-4xl mx-auto px-4 pt-8 sm:pt-12 pb-0" data-testid="is-dividend-stock-page">
      <BackOrHomeButton />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="mt-4 text-xs text-slate-500 dark:text-slate-400" data-testid="is-dividend-breadcrumb">
        <Link to="/" className="hover:text-cyan-600 dark:hover:text-cyan-400">Home</Link>
        <span className="mx-2 opacity-50">/</span>
        <Link to="/screener" className="hover:text-cyan-600 dark:hover:text-cyan-400">Screener</Link>
        <span className="mx-2 opacity-50">/</span>
        <span className="text-slate-700 dark:text-slate-300">Is {ticker} a dividend stock?</span>
      </nav>

      {/* H1 with exact-match keyword */}
      <h1 className="mt-4 text-3xl sm:text-4xl font-black text-slate-900 dark:text-slate-100 leading-tight" data-testid="is-dividend-h1">
        Is {ticker} a dividend stock?
      </h1>
      {data.name && data.name !== ticker && (
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400" data-testid="is-dividend-company-name">
          {data.name}
          {data.sector ? <span className="opacity-60"> · {data.sector}</span> : null}
        </p>
      )}

      {/* Verdict card — the answer above the fold */}
      <div
        className={`mt-6 p-6 sm:p-7 rounded-xl border-2 flex items-start gap-4 ${
          isDividend
            ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-700/50'
            : isMmf || isMutualFund
              ? 'bg-cyan-50 dark:bg-cyan-950/30 border-cyan-300 dark:border-cyan-700/50'
              : 'bg-rose-50 dark:bg-rose-950/30 border-rose-300 dark:border-rose-800/50'
        }`}
        data-testid="is-dividend-verdict-card"
      >
        <div className="shrink-0">
          {isDividend ? (
            <Check className="w-10 h-10 text-emerald-600 dark:text-emerald-400" strokeWidth={3} data-testid="is-dividend-verdict-icon-yes" />
          ) : isMmf ? (
            <DollarSign className="w-10 h-10 text-cyan-600 dark:text-cyan-400" strokeWidth={2.4} data-testid="is-dividend-verdict-icon-mmf" />
          ) : isMutualFund ? (
            <Info className="w-10 h-10 text-cyan-600 dark:text-cyan-400" strokeWidth={2.4} data-testid="is-dividend-verdict-icon-mutual-fund" />
          ) : (
            <X className="w-10 h-10 text-rose-600 dark:text-rose-400" strokeWidth={3} data-testid="is-dividend-verdict-icon-no" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-slate-100 leading-tight" data-testid="is-dividend-verdict-headline">
            {data.headline}
          </div>
          {isDividend && data.dividend_yield != null && (
            <div className="mt-2 text-sm text-slate-700 dark:text-slate-300">
              Current yield <strong>{fmtPct(data.dividend_yield)}</strong> · Annual rate <strong>${fmtNum(data.dividend_rate)}</strong>
              {data.payment_frequency ? <> · Pays <strong>{data.payment_frequency}</strong></> : null}
            </div>
          )}
          {isMmf && data.mmf_7day_yield != null && (
            <div className="mt-2 text-sm text-slate-700 dark:text-slate-300">
              Current 7-day SEC yield <strong>{fmtPct(data.mmf_7day_yield)}</strong>
              {data.mmf_aum ? <> · AUM <strong>${fmtAum(data.mmf_aum)}</strong></> : null}
            </div>
          )}
          {isMutualFund && (
            <div className="mt-2 text-sm text-slate-700 dark:text-slate-300">
              {ticker} is an open-end <strong>mutual fund</strong>. Most stock and
              bond mutual funds pay an income distribution (commonly quarterly)
              plus a capital gains distribution near year end &mdash; but{' '}
              <strong>DiviDrip does not track mutual fund distributions</strong>,
              because no data source we read carries them. We make no claim
              about {ticker}&apos;s payouts either way: check the fund family or
              your brokerage statement.{' '}
              <Link to="/learn/mutual-fund-tickers" className="underline">How
              single-X mutual funds work</Link>.
            </div>
          )}
        </div>
      </div>

      {/* Key numbers block — only for dividend payers */}
      {isDividend && (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="is-dividend-key-numbers">
          <KeyStat label="Yield" value={fmtPct(data.dividend_yield)} icon={TrendingUp} testId="is-dividend-stat-yield" />
          <KeyStat label="Annual rate" value={`$${fmtNum(data.dividend_rate)}`} icon={DollarSign} testId="is-dividend-stat-rate" />
          {/* Oct 2026 — most CURRENT ex-div (upcoming, or the one that just passed)
              — same date the /ticker/:t/ex-dividend-date page and the modal show. */}
          <KeyStat label="Ex-div" value={fmtDate(data.next_ex_dividend_date || data.ex_dividend_date)} icon={Calendar} testId="is-dividend-stat-exdiv"
            badge={<DeclaredPill declared={data.next_schedule ? data.next_schedule.declared : null} testId="is-dividend-exdiv-status" />} />
          <KeyStat label="Next pay" value={fmtDate(data.next_payment_date)} icon={Calendar} testId="is-dividend-stat-nextpay"
            badge={<DeclaredPill declared={data.next_schedule ? data.next_schedule.declared : null} testId="is-dividend-nextpay-status" />} />
        </div>
      )}
      {isDividend && data.next_schedule && !data.next_schedule.declared && (
        <div className="mt-3 rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50/70 dark:bg-amber-950/20 px-3 py-2">
          <EstimateDisclaimer next={data.next_schedule} testId="is-dividend-estimate-disclaimer" />
        </div>
      )}

      {/* Oct 2026 — Asset Class card for preferreds / baby bonds (same card as the Stock Modal). */}
      {isDividend && isFixedRateAsset(data) && (
        <div className="mt-6" data-testid="is-dividend-fixed-rate-card">
          <FixedRateAssetCard stock={data} />
        </div>
      )}

      {/* Recent ex-div dates — identical table to the ex-dividend-date page */}
      {isDividend && Array.isArray(data.ex_div_timeline) && data.ex_div_timeline.length > 0 && (
        <div className="mt-8" data-testid="is-dividend-timeline">
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">
            {ticker}&apos;s recent ex-dividend dates
          </h2>
          <SplitNote
            splits={data.splits}
            since={data.ex_div_timeline[data.ex_div_timeline.length - 1]?.date}
            suffix="— amounts in this table are split-adjusted"
            testId="is-dividend-split-note"
            className="mb-3"
          />
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900/70 text-left text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-2 font-semibold">Ex-dividend date</th>
                  <th className="px-4 py-2 font-semibold text-right">Amount per share</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {data.ex_div_timeline.map((row, i) => (
                  <tr
                    key={i}
                    className={`${i % 2 === 0 ? 'bg-emerald-100/80 dark:bg-emerald-950/25' : 'bg-sky-100/80 dark:bg-sky-950/25'} hover:brightness-95 dark:hover:brightness-125 transition-[filter]`}
                    data-testid={`is-dividend-timeline-row-${i}`}
                  >
                    <td className="px-4 py-2.5 text-slate-800 dark:text-slate-200 font-mono">{fmtDate(row.date)}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-emerald-600 dark:text-emerald-400">${fmtNum(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Growth streak strip — only if the ticker has a growth streak */}
      {isDividend && data.years_of_growth ? (
        <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 text-sm text-amber-800 dark:text-amber-300" data-testid="is-dividend-growth-streak">
          <TrendingUp className="w-4 h-4" />
          <span>
            <strong>{data.years_of_growth}-year</strong> dividend growth streak
            {data.tier ? <> · <strong>{titleCase(data.tier)}</strong></> : null}
          </span>
        </div>
      ) : null}

      {/* Why-not explanation for non-dividend payers */}
      {isNonDiv && (
        <div className="mt-6 p-5 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800" data-testid="is-dividend-why-not">
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-slate-500" /> Why {ticker} doesn&apos;t pay a dividend
          </h2>
          <p className="mt-2 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            {data.verdict === 'structural_non_dividend'
              ? <>{ticker} is classified as a <strong>{(data.non_dividend_security_class || 'special').toUpperCase()}</strong> security. These structures don&apos;t distribute recurring dividends by design — SPACs return trust proceeds if a deal isn&apos;t completed; warrants are convertible rights; units are pre-separation bundles.</>
              : <>{data.name || ticker} currently retains all earnings to fund growth, buybacks, or debt reduction. Many high-growth companies delay paying dividends until their business matures. This isn&apos;t inherently negative — capital appreciation can still deliver strong returns.</>}
          </p>
          <div className="mt-4 flex flex-wrap gap-3 text-xs">
            <Link to="/learn/valuation-non-dividend-stocks" className="inline-flex items-center gap-1 text-cyan-600 dark:text-cyan-400 hover:underline" data-testid="is-dividend-nondivlearn-1">
              <BookOpen className="w-3.5 h-3.5" /> Valuing non-dividend stocks
            </Link>
            <Link to="/learn/capital-appreciation-playbook" className="inline-flex items-center gap-1 text-cyan-600 dark:text-cyan-400 hover:underline" data-testid="is-dividend-nondivlearn-2">
              <BookOpen className="w-3.5 h-3.5" /> Capital appreciation playbook
            </Link>
            <Link to="/learn/lifecycle-of-a-stock" className="inline-flex items-center gap-1 text-cyan-600 dark:text-cyan-400 hover:underline" data-testid="is-dividend-nondivlearn-3">
              <BookOpen className="w-3.5 h-3.5" /> Lifecycle of a stock
            </Link>
          </div>
        </div>
      )}

      {/* MMF explainer */}
      {isMmf && (
        <div className="mt-6 p-5 rounded-xl bg-cyan-50 dark:bg-cyan-950/20 border border-cyan-200 dark:border-cyan-900/50" data-testid="is-dividend-mmf-explain">
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">How money market funds pay you</h2>
          <p className="mt-2 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            Money market funds like {ticker} hold short-duration Treasuries, commercial paper, and repos. They pay <strong>interest</strong> (technically an income distribution) that shows up in your account monthly. The <strong>7-day SEC yield</strong> annualizes the past week&apos;s income — it&apos;s the standard, apples-to-apples comparison across MMFs.
          </p>
          <Link to="/learn/money-market-funds" className="inline-flex items-center gap-1 mt-3 text-sm text-cyan-600 dark:text-cyan-400 hover:underline" data-testid="is-dividend-mmf-learnmore">
            <BookOpen className="w-3.5 h-3.5" /> Full money market fund guide
          </Link>
        </div>
      )}

      {/* Peer alternatives — internal linking */}
      {Array.isArray(data.peer_alternatives) && data.peer_alternatives.length > 0 && (
        <div className="mt-8" data-testid="is-dividend-peers">
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">
            {isDividend
              ? `Other dividend stocks like ${ticker}`
              : `Dividend-paying alternatives in the same sector`}
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.peer_alternatives.map((peer, idx) => {
              const peerTicker = (peer.ticker || '').toUpperCase();
              if (!peerTicker) return null;
              return (
                <Link
                  key={peerTicker}
                  to={`/is/${peerTicker.toLowerCase()}/a-dividend-stock`}
                  className="block p-4 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-cyan-400 dark:hover:border-cyan-600 hover:bg-slate-50 dark:hover:bg-slate-900/60 transition-colors group"
                  data-testid={`is-dividend-peer-${idx}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-bold text-slate-900 dark:text-slate-100 font-mono">{peerTicker}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{peer.name || peer.sector || ''}</div>
                    </div>
                    <div className="text-right shrink-0">
                      {peer.dividend_yield != null && (
                        <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{fmtPct(peer.dividend_yield)}</div>
                      )}
                      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-cyan-500 mt-1 ml-auto transition-transform group-hover:translate-x-0.5" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* CTA row into the rest of the app */}
      <div className="mt-10 p-6 rounded-xl bg-gradient-to-br from-cyan-50 to-emerald-50 dark:from-slate-900 dark:to-slate-900/60 border border-slate-200 dark:border-slate-800" data-testid="is-dividend-cta-row">
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-3">Take the next step</h2>
        {isDividend && (
          <div className="mb-3">
            <Link to={`/ticker/${tickerLower}/ex-dividend-date`} className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="is-dividend-cta-exdiv">
              <CalendarDays className="w-4 h-4" /> When is {ticker}&rsquo;s next ex-dividend date?
            </Link>
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          <Link to="/screener" className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg" data-testid="is-dividend-cta-screener">
            <Search className="w-4 h-4" /> Open Screener
          </Link>
          <button type="button" onClick={() => openStock(ticker, 'dividend-info')} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-white dark:text-slate-900 text-white font-semibold rounded-lg" data-testid="is-dividend-cta-open-modal">
            <DollarSign className="w-4 h-4" /> Open full dividend history
          </button>
          <Link to="/learn" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="is-dividend-cta-learn">
            <GraduationCap className="w-4 h-4" /> Learn dividends
          </Link>
        </div>
      </div>

      {/* FAQ section (also emits JSON-LD in useEffect above) */}
      <FaqSection ticker={ticker} data={data} />
      </div>
      <SiteDisclaimer />
      {stockModal}
    </>
  );
}

/* ─── Sub-components ────────────────────────────────────────────── */

function KeyStat({ label, value, icon: Icon, testId, badge = null }) {
  return (
    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">
        <Icon className="w-3 h-3" /> {label}{badge}
      </div>
      <div className="mt-1 text-lg font-bold text-slate-900 dark:text-slate-100">
        {value || '—'}
      </div>
    </div>
  );
}

function FaqSection({ ticker, data }) {
  const faqs = buildFaqs(ticker, data);
  return (
    <div className="mt-10" data-testid="is-dividend-faq">
      <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">Frequently asked</h2>
      <div className="space-y-3">
        {faqs.map((f, i) => (
          <details key={i} className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 group" data-testid={`is-dividend-faq-item-${i}`}>
            <summary className="cursor-pointer font-semibold text-slate-900 dark:text-slate-100 list-none flex items-start justify-between gap-3">
              <span>{f.q}</span>
              <ArrowRight className="w-4 h-4 mt-1 shrink-0 text-slate-400 group-open:rotate-90 transition-transform" />
            </summary>
            <p className="mt-3 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </div>
  );
}

/* ─── Content helpers ───────────────────────────────────────────── */

function buildFaqs(ticker, data) {
  const faqs = [];
  const name = data.name || ticker;

  if (data.verdict === 'dividend') {
    faqs.push({
      q: `Does ${ticker} pay a dividend?`,
      a: `Yes. ${ticker} (${name}) currently pays a dividend with a yield of ${fmtPct(data.dividend_yield)} and an annual rate of $${fmtNum(data.dividend_rate)} per share${data.payment_frequency ? `, distributed ${data.payment_frequency.toLowerCase()}` : ''}.`,
    });
    faqs.push({
      q: `How much does ${ticker} pay per share?`,
      a: `${ticker} pays approximately $${fmtNum(data.dividend_rate)} per share annually${data.next_payment_amount ? `, with a next payment of $${fmtNum(data.next_payment_amount)} per share` : ''}${data.next_payment_date ? ` on ${data.next_payment_date}` : ''}.`,
    });
    if (data.next_ex_dividend_date || data.ex_dividend_date) {
      faqs.push({
        q: `When is ${ticker}'s next ex-dividend date?`,
        a: `${ticker}'s most current ex-dividend date is ${fmtDate(data.next_ex_dividend_date || data.ex_dividend_date)}. You must own shares before this date to receive the upcoming dividend payment.`,
      });
    }
    if (data.years_of_growth) {
      faqs.push({
        q: `Has ${ticker} raised its dividend consistently?`,
        a: `${ticker} has a ${data.years_of_growth}-year dividend growth streak${data.tier ? ` and is classified as a Dividend ${titleCase(data.tier)}` : ''}. That means the company has increased its per-share dividend every year for at least ${data.years_of_growth} consecutive years.`,
      });
    }
  } else if (data.verdict === 'mutual_fund') {
    faqs.push({
      q: `Is ${ticker} a dividend stock?`,
      a: `${ticker} is not a stock at all — it is an open-end mutual fund. Most stock and bond mutual funds pay an income distribution (commonly quarterly) plus a capital gains distribution near year end, but DiviDrip does not track mutual fund distributions because no data source we read carries them. We show price and net assets only, and make no claim about ${ticker}'s payouts.`,
    });
    faqs.push({
      q: `Where can I find ${ticker}'s distribution history?`,
      a: `The fund family's own website publishes it, and it appears on your brokerage statement and your annual 1099-DIV. Mutual fund tickers are five letters ending in a single X; the five-letter double-XX funds are money market funds, and those DiviDrip does cover in full, including 7-day SEC yields sourced from SEC Form N-MFP.`,
    });
  } else if (data.verdict === 'money_market_fund') {
    faqs.push({
      q: `Is ${ticker} a dividend stock?`,
      a: `${ticker} is a money market fund, not a traditional dividend stock. It pays monthly income distributions (technically interest, not dividends) sourced from short-duration Treasuries and repos. Its current 7-day SEC yield is ${fmtPct(data.mmf_7day_yield)}.`,
    });
    faqs.push({
      q: `How is a money market fund different from a dividend stock?`,
      a: `Dividend stocks pay you a share of company profits, and the share price fluctuates. Money market funds like ${ticker} maintain a stable $1.00 NAV and pay interest income. The tradeoff: you give up upside for near-cash safety.`,
    });
  } else if (data.verdict === 'structural_non_dividend') {
    faqs.push({
      q: `Why doesn't ${ticker} pay a dividend?`,
      a: `${ticker} is classified as a ${(data.non_dividend_security_class || 'special').toUpperCase()} security. These structures don't distribute recurring dividends by design.`,
    });
    faqs.push({
      q: `What's the difference between ${ticker} and a regular stock?`,
      a: `SPACs, warrants, units, and rights are transitional or derivative securities. They don't have the recurring earnings stream that would fund a dividend. Their return comes from the underlying trust value or from converting into common shares at a favorable ratio.`,
    });
  } else {
    faqs.push({
      q: `Does ${ticker} pay a dividend?`,
      a: `No. ${ticker} (${name}) does not currently pay a dividend. The company retains earnings to fund growth, buybacks, or debt reduction — a common choice for growth-stage companies.`,
    });
    faqs.push({
      q: `Will ${ticker} ever pay a dividend?`,
      a: `Companies typically initiate dividends as they mature and their capital-return needs shift. There's no guarantee ${ticker} will pay one, but our system watches for new dividend initiations across our full 5,350+ ticker universe.`,
    });
    faqs.push({
      q: `What are dividend-paying alternatives to ${ticker}?`,
      a: data.peer_alternatives?.length
        ? `Consider ${data.peer_alternatives.slice(0, 3).map((p) => p.ticker).join(', ')} — dividend payers in the same or similar sector. Full comparison available in the DiviDrip screener.`
        : `Use the DiviDrip Screener to filter dividend payers in ${data.sector || 'the same sector'} — filter by yield, quality score, and payout ratio.`,
    });
  }

  // Universal FAQ appended for every verdict.
  faqs.push({
    q: `Where does DiviDrip's dividend data come from?`,
    a: `DiviDrip aggregates data from SEC EDGAR filings, Massive Market Data (for real-time quotes and dividend calendars), and — for money market funds only — Yahoo Finance for 7-day yield and AUM. All 5,350+ tickers are classified into dividend, non-dividend, or money-market categories using automated rules that mirror SEC listing standards.`,
  });

  return faqs;
}

/* ─── Formatters ────────────────────────────────────────────────── */

function fmtPct(v) {
  if (v == null || isNaN(Number(v))) return '—';
  // API yields are already percent units (AAPL 0.33 = 0.33%) — never rescale.
  return `${Number(v).toFixed(2)}%`;
}
function fmtNum(v) {
  if (v == null || isNaN(Number(v))) return '—';
  // Match the stock modal: up to 4 decimals, trailing zeros trimmed, min 2.
  const s = Number(v).toFixed(4).replace(/0+$/, '');
  const [whole, dec = ''] = s.split('.');
  return `${whole}.${dec.length > 2 ? dec.padEnd(4, '0') : dec.padEnd(2, '0')}`;
}
function fmtDate(v) {
  if (!v) return '—';
  // ISO "2026-10-01" → local calendar date (NOT new Date(iso), which is UTC
  // midnight and renders a day early in US zones). Pre-formatted strings pass through.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      .toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  }
  return String(v).slice(0, 20);
}
function fmtAum(v) {
  if (v == null || isNaN(Number(v))) return '—';
  const n = Number(v);
  if (n >= 1e12) return `${(n / 1e12).toFixed(1)}T`;
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  return n.toLocaleString();
}
function titleCase(s) {
  if (!s) return '';
  return String(s).replace(/\b\w/g, (c) => c.toUpperCase());
}
